import type { AsyncResourceVariant } from "../../index.js";
import {
  AggregateMetaData,
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import { ContractDetailed, Customer } from "../../index.js";
import { provideReact } from "../../react/index.js";
import { DateTime } from "luxon";
import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound";
import { config } from "../../config/config.js";
import type {
  FinderProfileData,
  FinderProfileListModelQueryData,
  FinderProfileTariffOptions,
} from "./types";

export class FinderProfile extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "leadfinder",
    "finderprofile",
  );

  public static ofCustomer(customer: Customer | string): FinderProfile {
    return this.ofCustomerId(extractId(customer));
  }

  public static ofCustomerId(customerId: string): FinderProfile {
    return new FinderProfile(customerId);
  }

  public static find = provideReact(
    async (customerId: string): Promise<FinderProfile | undefined> => {
      const data = await config.behaviors.finderProfile.find(customerId);
      if (data !== undefined) {
        return new FinderProfileDetailed(data);
      }
    },
  );

  public static get = provideReact(
    async (customerId: string): Promise<FinderProfile> => {
      const data = await this.find(customerId);
      assertObjectFound(data, "finderProfile", customerId);
      return data;
    },
  );

  public findDetailed = provideReact(
    () => FinderProfile.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<FinderProfileDetailed | undefined>>;

  public getDetailed = provideReact(
    () => FinderProfile.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<FinderProfileDetailed>>;

  public getContract = provideReact(async () => {
    const data = await config.behaviors.finderProfile.findContract(this.id);
    if (data) {
      return new ContractDetailed(data);
    }
  }, [this.id]) as AsyncResourceVariant<
    () => Promise<ContractDetailed | undefined>
  >;

  public static query(query: FinderProfileListModelQueryData = {}) {
    return new FinderProfileListQuery(query);
  }
}

class FinderProfileCommon extends classes(
  DataModel<FinderProfileData>,
  FinderProfile,
) {
  public readonly customerId: string;
  public readonly customer: Customer;
  public readonly domain: string;
  public readonly url: string;
  public readonly disabledAt: DateTime | undefined;
  public readonly isDisabled: boolean;
  public readonly approvedAt: DateTime | undefined;
  public readonly tariff: FinderProfileTariffOptions;
  public readonly unlockContingentRenewalDate: DateTime | undefined;

  public constructor(data: FinderProfileData) {
    super([data], [data.customerId]);
    this.customerId = data.customerId;
    this.customer = Customer.ofId(data.customerId);
    this.domain = data.domain;
    this.url = data.domain.startsWith("https://")
      ? data.domain
      : `https://${data.domain}`;
    this.disabledAt = data.disabledOn
      ? DateTime.fromISO(data.disabledOn)
      : undefined;
    this.approvedAt = data.approvedOn
      ? DateTime.fromISO(data.approvedOn)
      : undefined;
    this.isDisabled = !!data.disabledOn;
    this.tariff = data.tariff;
    this.unlockContingentRenewalDate = data.tariff.nextUnlockRenewalDate
      ? DateTime.fromISO(data.tariff.nextUnlockRenewalDate)
      : undefined;
  }

  public hasAccess = () => {
    return !this.isDisabled;
  };
}

export class FinderProfileDetailed extends classes(
  FinderProfileCommon,
  DataModel<FinderProfileData>,
) {
  public constructor(data: FinderProfileData) {
    super([data], [data]);
  }
}

export class FinderProfileListItem extends classes(
  DataModel<FinderProfileData>,
  FinderProfile,
) {
  public constructor(data: FinderProfileData) {
    super([data], [data.customerId]);
  }
}

export class FinderProfileListQuery extends ListQueryModel<FinderProfileListModelQueryData> {
  public constructor(query: FinderProfileListModelQueryData) {
    super(query);
  }

  public refine(query: FinderProfileListModelQueryData) {
    return new FinderProfileListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.finderProfile.list();

    return new FinderProfileList(
      this.query,
      items.map((d) => new FinderProfileListItem(d)),
    );
  }, [this.queryId, "finderProfile"]);
}

export class FinderProfileList extends classes(
  FinderProfileListQuery,
  ListDataModel<FinderProfileListItem>,
) {
  public constructor(
    query: FinderProfileListModelQueryData,
    items: FinderProfileListItem[],
  ) {
    super([query], [items, items.length]);
  }
}
